/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React, { useId, useState } from 'react'
import clsx from 'clsx'

export interface UiCurrencyInputProps {
    label?: React.ReactNode
    error?: string
    hasError?: boolean
    value?: number
    onValueChange?: (value: string | undefined) => void
    prefix?: string
    decimalsLimit?: number
    placeholder?: string
    disabled?: boolean
    className?: string
    containerClassName?: string
    id?: string
}

const UiCurrencyInput = React.forwardRef<
    HTMLInputElement,
    UiCurrencyInputProps
>(
    (
        {
            label,
            error,
            hasError,
            value,
            onValueChange,
            prefix = 'CHF ',
            decimalsLimit = 2,
            placeholder = '0.00',
            disabled,
            className,
            containerClassName,
            id,
        },
        ref
    ) => {
        const generatedId = useId()
        const inputId = id || generatedId
        const errorId = `${inputId}-error`

        const [displayValue, setDisplayValue] = useState<string>(() => {
            if (value === undefined || value === null) return ''
            return value.toFixed(decimalsLimit)
        })

        const formatValue = (val: string): string => {
            if (!val) return ''
            const num = parseFloat(val)
            if (isNaN(num)) return ''
            return num.toFixed(decimalsLimit)
        }

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            let inputValue = e.target.value

            inputValue = inputValue.replace(/[^0-9.,]/g, '')
            inputValue = inputValue.replace(',', '.')

            const parts = inputValue.split('.')
            if (parts.length > 2) {
                inputValue = parts[0] + '.' + parts.slice(1).join('')
            }

            if (parts[1] && parts[1].length > decimalsLimit) {
                inputValue = parts[0] + '.' + parts[1].slice(0, decimalsLimit)
            }

            setDisplayValue(inputValue)

            if (onValueChange) {
                if (inputValue === '' || inputValue === '.') {
                    onValueChange(undefined)
                } else {
                    onValueChange(inputValue)
                }
            }
        }

        const handleBlur = () => {
            if (displayValue && displayValue !== '.') {
                const formatted = formatValue(displayValue)
                setDisplayValue(formatted)
                if (onValueChange && formatted) {
                    onValueChange(formatted)
                }
            }
        }

        React.useEffect(() => {
            if (value !== undefined && value !== null) {
                const formatted = value.toFixed(decimalsLimit)
                if (
                    displayValue !== formatted &&
                    document.activeElement?.id !== inputId
                ) {
                    setDisplayValue(formatted)
                }
            }
        }, [value, decimalsLimit, displayValue, inputId])

        return (
            <div className={clsx('UiCurrencyInput flex flex-col gap-1.5', containerClassName)}>
                {label && (
                    <label
                        className="text-sm font-medium text-gray-700 dark:text-gray-300"
                        htmlFor={inputId}
                    >
                        {label}
                    </label>
                )}
                <div className="relative">
                    {prefix && (
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500 dark:text-gray-400 pointer-events-none text-sm">
                            {prefix}
                        </div>
                    )}
                    <input
                        ref={ref}
                        id={inputId}
                        type="text"
                        inputMode="decimal"
                        value={displayValue}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder={placeholder}
                        disabled={disabled}
                        aria-invalid={!!error || !!hasError}
                        aria-describedby={error ? errorId : undefined}
                        className={clsx(
                            'flex h-10 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-500 focus-visible:outline-none focus-visible:border-blue-500 focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-800 dark:bg-gray-950 dark:text-gray-100 dark:placeholder:text-gray-400 dark:focus-visible:border-blue-400',
                            prefix && 'pl-12',
                            error || hasError
                                ? 'border-red-500 focus-visible:border-blue-500 focus-visible:ring-0'
                                : 'focus-visible:border-blue-500 focus-visible:ring-0',
                            className
                        )}
                    />
                </div>
                {error && (
                    <span
                        id={errorId}
                        className="text-xs text-red-500 mt-1"
                        aria-live="polite"
                    >
                        {error}
                    </span>
                )}
            </div>
        )
    }
)

UiCurrencyInput.displayName = 'UiCurrencyInput'

export default UiCurrencyInput
